// URL Utilities - Slug generation and URL parameter handling

import { cellSlug } from './cellSlug.js'

/**
 * Generate URL-safe slug from cell data (the slug.py rule, see ./cellSlug.js)
 * @param {Object} cell - Cell data with manufacturer and name
 * @returns {string} - URL slug (e.g., "samsung-inr18650-25r")
 */
export function generateCellSlug(cell) {
  return cellSlug(cell)
}

/**
 * Parse slug back to search terms
 * @param {string} slug - URL slug
 * @returns {Object} - { manufacturer, model }
 */
export function parseSlugToSearchTerms(slug) {
  if (!slug) return { manufacturer: '', model: '' }
  
  // Split on first hyphen (manufacturer-model pattern)
  const parts = slug.split('-')
  
  // Heuristic: First part is manufacturer, rest is model
  // This is imperfect but works for most cases
  const manufacturer = parts[0] || ''
  const model = parts.slice(1).join('-') || ''
  
  return {
    manufacturer: manufacturer.charAt(0).toUpperCase() + manufacturer.slice(1),
    model: model.toUpperCase()
  }
}

/**
 * Get current URL parameters
 * @returns {Object} - { basis, domain, theme }
 */
export function getUrlParams() {
  if (typeof window === 'undefined') return {}
  
  const params = new URLSearchParams(window.location.search)
  
  return {
    basis: params.get('basis') || null,        // 'abs' | 'grav' | 'vol'
    domain: params.get('domain') || null,      // 'ep' | 'cc'
    theme: params.get('theme') || null,        // 'light' | 'dark' | 'auto'
  }
}

/**
 * Update URL parameters without page reload
 * @param {Object} params - { basis, domain, theme }
 */
export function updateUrlParams(params) {
  if (typeof window === 'undefined') return
  
  const url = new URL(window.location)
  
  // Update or remove parameters
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      url.searchParams.delete(key)
    } else {
      url.searchParams.set(key, value)
    }
  })
  
  // Replace state without reload
  window.history.replaceState({}, '', url)
}

/**
 * Convert domain value to URL format
 * @param {string} domain - 'energy-power' | 'capacity-current'
 * @returns {string} - 'ep' | 'cc'
 */
export function domainToUrlParam(domain) {
  return domain === 'energy-power' ? 'ep' : 'cc'
}

/**
 * Convert URL format to domain value
 * @param {string} param - 'ep' | 'cc'
 * @returns {string} - 'energy-power' | 'capacity-current'
 */
export function urlParamToDomain(param) {
  return param === 'cc' ? 'capacity-current' : 'energy-power'
}
